import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { APP_FACTORY_QUERY_KEYS, type FactoryAssetPlatform } from '@/lib/constants/appFactory';
import type { FactoryBuildRequestReq } from '@/lib/types/AppFactory';
import { AppFactoryApi } from './AppFactoryApi';
import { AppBuildRequestUtils } from './AppBuildRequestUtils';

// Factory reads degrade quietly: when the gateway is disabled or the daemon is
// unreachable the routes error, so retries are pointless — components render
// an "unavailable" state off isError instead.
const FACTORY_QUERY_OPTS = {
  retry: false,
  staleTime: 30_000,
  refetchOnWindowFocus: false,
} as const;

export function useFactoryReleases(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.releases(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getReleases(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useFactoryTasks(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.tasks(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getTasks(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

/** While a build of the app is in flight the list re-reads on this cadence (a build takes minutes). */
const BUILDS_ACTIVE_POLL_MS = 20_000;

export function useFactoryBuilds(appCfgId: number, enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.builds(selectedCId ?? 0, appCfgId),
    queryFn: () => AppFactoryApi.getBuilds(selectedCId!, appCfgId),
    enabled: enabled && selectedCId != null && appCfgId > 0,
    ...FACTORY_QUERY_OPTS,
    // Holder-requested builds (3.34.0): the Status tab follows a queued build
    // to its resting state without a reload; idle lists never poll.
    refetchInterval: (query) =>
      AppBuildRequestUtils.hasActiveBuild(query.state.data?.builds ?? []) ? BUILDS_ACTIVE_POLL_MS : false,
  });
}

export function useFactorySubmissions(appCfgId: number, enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.submissions(selectedCId ?? 0, appCfgId),
    queryFn: () => AppFactoryApi.getSubmissions(selectedCId!, appCfgId),
    enabled: enabled && selectedCId != null && appCfgId > 0,
    ...FACTORY_QUERY_OPTS,
  });
}

/**
 * Holder-requested build (3.34.0): the farm's idempotent claim for one
 * platform + release. Every factory read of the account is invalidated on
 * success — the new row lands in the builds list, the tasks feed, and (once
 * its checks pass) the submissions.
 */
export function useRequestBuild(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: FactoryBuildRequestReq) =>
      AppFactoryApi.requestBuild(selectedCId!, appCfgId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

/**
 * Click-triggered presigned downloads (Tier 1). A mutation, not a query:
 * the URLs are short-lived, so they are minted fresh per click and never
 * cached.
 */
export function useSubmissionDownloads(appCfgId: number) {
  const { selectedCId } = useAuth();
  return useMutation({
    mutationFn: (submissionId: string) =>
      AppFactoryApi.getSubmissionDownloads(selectedCId!, appCfgId, submissionId),
  });
}

// ── Store accounts (4.2 delegated store accounts) ──

export function useStoreAccounts(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.storeAccounts(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getStoreAccounts(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useCreateStoreAccount() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: Parameters<typeof AppFactoryApi.createStoreAccount>[1]) =>
      AppFactoryApi.createStoreAccount(selectedCId!, req),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_FACTORY_QUERY_KEYS.storeAccounts(selectedCId!),
      });
    },
  });
}

export function useTransitionStoreAccount() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      appStoreAccountId,
      from,
      to,
      note,
    }: {
      appStoreAccountId: string;
      from: string;
      to: string;
      note?: string;
    }) => AppFactoryApi.transitionStoreAccount(selectedCId!, appStoreAccountId, { from, to, note }),
    onSuccess: () => {
      // The tasks feed branches its text on the account state too.
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

/**
 * Tenant-scoped submission transition (B3): the holder records the portal
 * steps on its own app; the api derives the actor and scopes the id.
 */
export function useTransitionOwnSubmission(appCfgId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      from,
      to,
      note,
    }: {
      submissionId: string;
      from: string;
      to: string;
      note?: string;
    }) =>
      AppFactoryApi.transitionOwnSubmission(selectedCId!, appCfgId, submissionId, { from, to, note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

// ── Brand kit (asset sets) ──
//
// One platform's approval history and current images per read (3.34.0): the
// factory stages and promotes each platform's set under its own folder, so
// the platform is part of the key and of every call.

export function useFactoryAssetSets(appCfgId: number, platform: FactoryAssetPlatform, enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.assetSets(selectedCId ?? 0, appCfgId, platform),
    queryFn: () => AppFactoryApi.getAssetSets(selectedCId!, appCfgId, platform),
    enabled: enabled && selectedCId != null && appCfgId > 0,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useCreateAssetSet(appCfgId: number, platform: FactoryAssetPlatform) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: Parameters<typeof AppFactoryApi.createAssetSet>[2]) =>
      AppFactoryApi.createAssetSet(selectedCId!, appCfgId, req, platform),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_FACTORY_QUERY_KEYS.assetSets(selectedCId!, appCfgId, platform),
      });
    },
  });
}

export function useDecideAssetSet(appCfgId: number, platform: FactoryAssetPlatform) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ assetSetId, state }: { assetSetId: string; state: 'approved' | 'rejected' }) =>
      AppFactoryApi.decideAssetSet(selectedCId!, appCfgId, assetSetId, state),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: APP_FACTORY_QUERY_KEYS.assetSets(selectedCId!, appCfgId, platform),
      });
    },
  });
}

// ── Operator surface (server-side allowlist; 403 for everyone else) ──

export function useFactoryDevices(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.devices(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getDevices(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

/**
 * Is the signed-in login a platform operator? There is deliberately no
 * client-side role: the api's operator allowlist is the authority, so this
 * asks an operator-only read (the farm devices, which the console shares) and
 * treats anything but a success as "no" — fail closed, including while it
 * loads or when the factory is unreachable. Logins without media access are
 * never operators, so they do not ask at all.
 */
export function useIsFactoryOperator(): boolean {
  const { authData } = useAuth();
  const devicesQuery = useFactoryDevices(!!authData?.cpuser.permissions.serviceRead);
  return devicesQuery.isSuccess;
}

export function useFactoryActiveSubmissions(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.activeSubmissions(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getActiveSubmissions(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useFactoryCampaigns(enabled = true) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.campaigns(selectedCId ?? 0),
    queryFn: () => AppFactoryApi.getCampaigns(selectedCId!),
    enabled: enabled && selectedCId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useFactoryCampaignTargets(campaignId: string | null) {
  const { selectedCId } = useAuth();
  return useQuery({
    queryKey: APP_FACTORY_QUERY_KEYS.campaignTargets(selectedCId ?? 0, campaignId ?? ''),
    queryFn: () => AppFactoryApi.getCampaignTargets(selectedCId!, campaignId!),
    enabled: selectedCId != null && campaignId != null,
    ...FACTORY_QUERY_OPTS,
  });
}

export function useAdvanceCampaign() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => AppFactoryApi.advanceCampaign(selectedCId!, campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

export function useAbortCampaign() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => AppFactoryApi.abortCampaign(selectedCId!, campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}

export function useTransitionSubmission() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      submissionId,
      from,
      to,
      note,
    }: {
      submissionId: string;
      from: string;
      to: string;
      note?: string;
    }) => AppFactoryApi.transitionSubmission(selectedCId!, submissionId, { from, to, note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_FACTORY_QUERY_KEYS.all });
    },
  });
}
